import type { QueriesResults, UseQueryOptions } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { isNotNullish } from '@adflow/shared/util-js';

interface IdObject {
	id: number;
}

export function useTableDataWithTempRows<T extends IdObject>(
	queryData: T[] | undefined,
	rowsPerPage: number,
	openRows: (string | number)[] = [],
	queryOptionsForRow: (
		id: number
	) => Omit<UseQueryOptions<T | undefined>, 'context'>
) {
	/**
	 * Rows that are open but not in the query data. This can happen
	 * when
	 * 1. A user navigates to this page from a notification or a link
	 * 2. The user visits a page from their browser history but the open
	 * 		entity is no longer in that page
	 *
	 * Note: Because this is based on the `openRows` query param, the
	 * fetched rows will disappear from the table after the user closes
	 * the row
	 */
	const missingRows = useMemo(() => {
		if (!queryData) {
			// Don't mark any rows as missing until query data is loaded
			return [];
		}
		const tableDataIds = new Set(queryData.map(({ id }) => id));
		return (
			openRows
				.map((rowId) =>
					typeof rowId === 'string' ? Number.parseInt(rowId) : rowId
				)
				// eslint-disable-next-line unicorn/prefer-number-properties
				.filter((rowId) => !isNaN(rowId))
				.filter((rowId) => !tableDataIds.has(rowId))
		);
	}, [openRows, queryData]);

	const queries = missingRows.map(queryOptionsForRow);

	/**
	 * The current data visible in the table, including any temp rows
	 */
	const tableData: T[] = useQueries({
		queries,
		combine: useCallback(
			(missingRowQueries: QueriesResults<typeof queries>) => {
				const missingRowData = missingRowQueries // TODO: It would be nice to keep the rows in the same order as much as possible.
					.sort((a, b) => b.dataUpdatedAt - a.dataUpdatedAt)
					.map((query) => query.data)
					.filter(isNotNullish)
					.filter((data): data is T => data !== undefined);
				return [...missingRowData, ...(queryData ?? [])].slice(
					0,
					rowsPerPage ?? 0
				);
			},
			[queryData, rowsPerPage]
		),
	});

	return { tableData };
}
